import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionBar,
  ActionMenu,
  Form,
  ModalBuilder,
  Table,
  useFormField,
} from "@redriver/cinnamon";

import { sendForCustomerApproval } from "./actions";
import { Button, DropdownItem, Icon } from "semantic-ui-react";
import { TranslatedModal } from "features/../../../shared/components/modals";
import { DirectDebitSignatory } from "features/../../../shared/constants/enums";
import OptionsStep from "features/Agreements/AgreementsList/SheetActions/SendForApproval/OptionsStep";
import ConfirmationStep from "features/Agreements/AgreementsList/SheetActions/SendForApproval/ConfirmationStep";

const SendForApproval = ({
  sheet,
  sheetData,
  onSubmitted,
  scheduleReference,
  agreementReference,
  sheetActionRequired,
  generateDocument,
  boilerPlateTandCsId,
  triggerDisabled,
}) => {
  const { t } = useTranslation("agreements");
  const { t: tSheet } = useTranslation("sheetTranslation");

  const [directDebitSignatory] = useFormField("directDebitSignatory");
  const [totalDirectDebitSignatories, setTotalDirectDebitSignatories] =
    useState(0);
  const [includeSecondClientSignatory, setIncludeSecondClientSignatory] =
    useState(false);

  const sheetId = sheet ? sheet.sheetId : sheetData.id;

  const [stepNumber, setStepNumber] = useState(0);
  const optionsStep = stepNumber === 0;
  const confirmationStep = stepNumber === 1;
  const hasPreviousStep = stepNumber > 0;
  const hasNextStep = stepNumber < 1;
  const canSubmit = stepNumber === 1;

  const availableActions = process.env.TRADE_AS_IPS
    ? sheet
      ? sheet.availableActionsIps
      : sheetData.availableActionsIps
    : sheet
    ? sheet.availableActionsPrincipal
    : sheetData.availableActionsPrincipal;

  if (!availableActions[sheetActionRequired]) return null;

  const DirectDebitMandateOptions = [
    {
      value: DirectDebitSignatory.SameAsAuthorisedSignatory,
      text: t("SheetActions.SendForApproval.SameAsAuthorisedSignatory"),
    },
    {
      value: DirectDebitSignatory.NewSignatory,
      text: t("SheetActions.SendForApproval.NewSignatory"),
    },
    {
      value: DirectDebitSignatory.NotRequired,
      text: t("SheetActions.SendForApproval.NotRequired"),
    },
  ];

  const goToNextStep = () => {
    setStepNumber(stepNumber + 1);
  };

  const goToPreviousStep = () => {
    setStepNumber(stepNumber - 1);
  };

  const disableNext =
    directDebitSignatory == DirectDebitSignatory.NewSignatory &&
    totalDirectDebitSignatories == 0;

  const missingCredit = sheet?.missingCredit ?? sheetData?.missingCredit;
  const submitDisabled =
    triggerDisabled ||
    missingCredit ||
    (!boilerPlateTandCsId && process.env.TRADE_AS_IPS);

  return (
    <ModalBuilder
      withForm
      initialData={{
        customerSignatory1: { isNew_customerSignatory1: false },
        customerSignatory2: { isNew_customerSignatory2: false },
      }}
      submitAction={sendForCustomerApproval}
      submitParams={{
        sheetId,
        includeSecondClientSignatory,
      }}
      onCancelled={() => {
        setTotalDirectDebitSignatories(0);
      }}
      onSubmitted={onSubmitted}
      renderTrigger={(trigger) =>
        generateDocument ? (
          <Button
            className="generate-btn"
            primary
            onClick={submitDisabled ? null : trigger}
            disabled={submitDisabled}
          >
            <Icon name="check" />
            {tSheet(
              `SendForApproval.${
                process.env.TRADE_AS_IPS
                  ? "TriggerText_Ips"
                  : "TriggerText_Principal"
              }`,
            )}
          </Button>
        ) : (
          <DropdownItem onClick={trigger} disabled={submitDisabled}>
            {t("SheetActions.SendForApproval.ActionLabel")}
          </DropdownItem>
        )
      }
      renderModal={(modalProps, formProps, state, events) => (
        <TranslatedModal.Edit
          {...modalProps}
          header={t("SheetActions.SendForApproval.ModalHeader")}
          className="custom-actions customer-approval"
          size="large"
          onClick={(event) => event && event.stopPropagation()}
        >
          <Form
            {...formProps}
            onClick={(event) => event && event.stopPropagation()}
          >
            {optionsStep && (
              <OptionsStep
                sheetId={sheetId}
                setTotalDirectDebitSignatories={setTotalDirectDebitSignatories}
                setIncludeSecondClientSignatory={
                  setIncludeSecondClientSignatory
                }
                includeSecondClientSignatory={includeSecondClientSignatory}
                totalDirectDebitSignatories={totalDirectDebitSignatories}
                DirectDebitMandateOptions={DirectDebitMandateOptions}
              />
            )}
            {confirmationStep && (
              <ConfirmationStep
                formData={formProps.value}
                agreementReference={agreementReference}
                scheduleReference={scheduleReference}
                sheetName={sheet ? sheet.sheetName : sheetData.name}
                includeSecondClientSignatory={includeSecondClientSignatory}
                dealerOfficeId={
                  sheet ? sheet.dealerOfficeId : sheetData.dealerOfficeId
                }
                t={t}
              />
            )}
            <div className="custom-action-footer">
              <ActionBar>
                <ActionBar.Item>
                  <Button
                    className="cancel"
                    content={t("Cancel")}
                    disabled={modalProps.submitting}
                    onClick={modalProps.onCancel}
                  />
                </ActionBar.Item>
                <ActionBar.Space />
                {hasPreviousStep && (
                  <ActionBar.Item fluid align="right">
                    <Button
                      className="previous"
                      content={t("Back")}
                      disabled={modalProps.submitting}
                      onClick={goToPreviousStep}
                    />
                  </ActionBar.Item>
                )}
                {hasNextStep && (
                  <ActionBar.Item
                    fluid={!hasPreviousStep} //prevent random whitespace
                    align="right"
                  >
                    <Button
                      primary
                      className="next"
                      content={t("Next")}
                      loading={state.submitting}
                      disabled={state.submitting || disableNext}
                      onClick={() => {
                        let { formValid } = state;
                        if (formValid) {
                          if (
                            formProps.value.directDebitSignatory !==
                            DirectDebitSignatory.NewSignatory
                          ) {
                            formProps.value.directDebitSignatories = null;
                            setTotalDirectDebitSignatories(0);
                          }
                          if (!includeSecondClientSignatory) {
                            formProps.value.customerSignatory2.authorisedSignatory =
                              null;
                            formProps.value.customerSignatory2.authorisedSignatoryId =
                              null;
                          }
                          goToNextStep();
                          return;
                        }
                        events.onSubmit();
                      }}
                    />
                  </ActionBar.Item>
                )}
                {canSubmit && (
                  <ActionBar.Item>
                    <Button
                      className="positive submit"
                      content={t("Confirm")}
                      loading={modalProps.submitting}
                      disabled={modalProps.submitting}
                      onClick={() => {
                        modalProps.submitting = true;
                        events.onSubmit();
                      }}
                    />
                  </ActionBar.Item>
                )}
              </ActionBar>
            </div>
          </Form>
        </TranslatedModal.Edit>
      )}
    />
  );
};

export default SendForApproval;
